/**
 * Merkezi Menu Matrisi
 * Sidebar menüleri RBAC matrisine bağlı şekilde filtrelenir
 *
 * Her menü item bir resource'a karşılık gelir
 * Permission kontrolü merkezi RBAC_MATRIX tarafından yapılır
 */

import { Resource } from './roles';

export interface MenuItemConfig {
  id: string;
  label: string;
  href?: string;
  icon?: string;
  /**
   * Bu menü item'ı hangi resource'a bağlı?
   * Permission kontrolünde bu resource kullanılır
   */
  resource: Resource;
  /**
   * Hangi action gerekli? (default: 'view')
   */
  requiredAction?: 'view' | 'manage';
  children?: MenuItemConfig[];
}

/**
 * Ana menu yapısı
 * Düzen mantıksal grouplar halinde organize edilmiş
 */
export const MENU_MATRIX: MenuItemConfig[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    href: '/',
    icon: 'LayoutDashboard',
    resource: 'dashboard',
  },

  // ============================================
  // ARAÇ GEREÇ GRUBU
  // ============================================
  {
    id: 'arac_gerec',
    label: 'Araç Gereç',
    icon: 'Settings',
    resource: 'companies', // Parent menü için most restrictive resource
    children: [
      {
        id: 'companies',
        label: 'Şirketler',
        href: '/companies',
        icon: 'Building2',
        resource: 'companies',
      },
      {
        id: 'locations',
        label: 'Lokasyonlar',
        href: '/locations',
        icon: 'MapPin',
        resource: 'locations',
      },
      {
        id: 'suppliers',
        label: 'Tedarikçiler',
        href: '/suppliers',
        icon: 'Truck',
        resource: 'suppliers',
      },
      {
        id: 'equipment',
        label: 'Ekipmanlar',
        href: '/equipment',
        icon: 'Package',
        resource: 'equipment',
      },
      {
        id: 'consumables',
        label: 'Sarf Malzemeler',
        href: '/consumables',
        icon: 'ShoppingCart',
        resource: 'consumables',
      },
      {
        id: 'documents',
        label: 'Evraklar',
        href: '/documents',
        icon: 'FileText',
        resource: 'documents',
      },
      {
        id: 'maintenance_contracts',
        label: 'Bakım Anlaşmaları',
        href: '/maintenance-contracts',
        icon: 'FileCheck',
        resource: 'maintenance-contracts',
      },
      {
        id: 'damage_theft',
        label: 'Hasar & Çalınma',
        href: '/damage-theft',
        icon: 'AlertTriangle',
        resource: 'damage-theft',
      },
    ],
  },

  // ============================================
  // SİPARİŞ & CAPEX
  // ============================================
  {
    id: 'orders',
    label: 'Siparişler',
    href: '/orders',
    icon: 'ShoppingCart',
    resource: 'orders',
  },
  {
    id: 'capex',
    label: 'CAPEX',
    href: '/capex',
    icon: 'DollarSign',
    resource: 'capex',
  },

  // ============================================
  // YAZICI YÖNETİMİ
  // ============================================
  {
    id: 'printers',
    label: 'Yazıcılar',
    href: '/printers',
    icon: 'Printer',
    resource: 'printers',
  },

  // ============================================
  // TEKNİK SERVİS
  // ============================================
  {
    id: 'technical_service',
    label: 'Teknik Servis',
    href: '/technical-service',
    icon: 'Wrench',
    resource: 'technical-service',
  },

  // ============================================
  // GSM & TELEFON
  // ============================================
  {
    id: 'gsm_lines',
    label: 'GSM&Telefon',
    icon: 'Smartphone',
    resource: 'gsm-lines',
    children: [
      {
        id: 'gsm_lines_main',
        label: 'GSM Hatları',
        href: '/gsm-lines',
        icon: 'Smartphone',
        resource: 'gsm-lines',
      },
      {
        id: 'mobile_phones',
        label: 'Cep Telefonları',
        href: '/mobile-phones',
        icon: 'Smartphone',
        resource: 'mobile-phones',
      },
      {
        id: 'brand_model_management',
        label: 'Marka/Model Yönetimi',
        href: '/brand-model-management',
        icon: 'Package',
        resource: 'brand-model-management',
      },
      {
        id: 'tariffs',
        label: 'Tarifeler',
        href: '/tariffs',
        icon: 'CreditCard',
        resource: 'tariffs',
      },
      {
        id: 'phone_replacements',
        label: 'Telefon Değiştirme',
        href: '/phone-replacements',
        icon: 'Smartphone',
        resource: 'phone-replacements',
      },
    ],
  },

  // ============================================
  // SUNUCULAR
  // ============================================
  {
    id: 'servers',
    label: 'Sunucular',
    icon: 'Server',
    resource: 'physical-servers',
    children: [
      {
        id: 'physical_servers',
        label: 'Fiziksel Sunucular',
        href: '/physical-servers',
        icon: 'Server',
        resource: 'physical-servers',
      },
      {
        id: 'virtual_servers',
        label: 'Sanal Sunucular',
        href: '/virtual-servers',
        icon: 'Cloud',
        resource: 'virtual-servers',
      },
    ],
  },

  // ============================================
  // ENDÜSTRIYEL
  // ============================================
  {
    id: 'industrial',
    label: 'Industrial',
    icon: 'Settings',
    resource: 'iot-devices',
    children: [
      {
        id: 'iot',
        label: 'IoT Cihazları',
        href: '/iot-devices',
        icon: 'Wifi',
        resource: 'iot-devices',
      },
      {
        id: 'scales',
        label: 'Akıllı Teraziler',
        href: '/scales',
        icon: 'Scale',
        resource: 'scales',
      },
      {
        id: 'radios',
        label: 'Telsizler',
        href: '/radios',
        icon: 'Radio',
        resource: 'radios',
      },
      {
        id: 'handheld_terminals',
        label: 'El Terminalleri',
        href: '/handheld-terminals',
        icon: 'Smartphone',
        resource: 'handheld-terminals',
      },
    ],
  },

  // ============================================
  // AĞ YÖNETİMİ
  // ============================================
  {
    id: 'network',
    label: 'Ağ',
    icon: 'Network',
    resource: 'network-switches',
    children: [
      {
        id: 'network_switches',
        label: 'Network Switch',
        href: '/switches',
        icon: 'Network',
        resource: 'network-switches',
      },
      {
        id: 'cabinets',
        label: 'IT Kabinetleri',
        href: '/cabinets',
        icon: 'Shield',
        resource: 'cabinets',
      },
      {
        id: 'circuits',
        label: 'Devreler',
        href: '/circuits',
        icon: 'Wifi',
        resource: 'circuits',
      },
    ],
  },

  // ============================================
  // STORAGE
  // ============================================
  {
    id: 'storage',
    label: 'Depolama Birimleri',
    href: '/storage-units',
    icon: 'Database',
    resource: 'storage-units',
  },

  // ============================================
  // TOPLANTILER
  // ============================================
  {
    id: 'meetings',
    label: 'Toplantılar',
    href: '/meetings',
    icon: 'Calendar',
    resource: 'meetings',
  },

  // ============================================
  // ZİMMETLEME
  // ============================================
  {
    id: 'assignment_forms',
    label: 'Zimmet İşlemleri',
    icon: 'FileText',
    resource: 'assignment-forms',
    children: [
      {
        id: 'assignment_forms_main',
        label: 'Zimmet Formları',
        href: '/assignment-forms',
        icon: 'FileText',
        resource: 'assignment-forms',
      },
      {
        id: 'assignment_logs',
        label: 'Zimmetleme İşlem Logları',
        href: '/assignment-logs',
        icon: 'Activity',
        resource: 'assignment-logs',
      },
    ],
  },

  // ============================================
  // GÜVENLİK SİSTEMLERİ
  // ============================================
  {
    id: 'security_cameras',
    label: 'Güvenlik Sistemleri',
    icon: 'Camera',
    resource: 'cameras',
    children: [
      {
        id: 'cameras',
        label: 'Kameralar',
        href: '/cameras',
        icon: 'Camera',
        resource: 'cameras',
      },
      {
        id: 'camera-faults',
        label: 'Kamera Arızaları',
        href: '/camera-faults',
        icon: 'AlertTriangle',
        resource: 'camera-faults',
      },
      {
        id: 'nvrs',
        label: 'NVR\'ler',
        href: '/nvrs',
        icon: 'Video',
        resource: 'nvrs',
      },
    ],
  },

  // ============================================
  // AYARLAR
  // ============================================
  {
    id: 'settings',
    label: 'Ayarlar',
    icon: 'Settings',
    resource: 'users', // Parent için most restrictive
    children: [
      {
        id: 'assignment_definitions',
        label: 'Zimmet Tanımları',
        href: '/assignment-definitions',
        icon: 'FileText',
        resource: 'assignment-definitions',
      },
      {
        id: 'system_settings',
        label: 'Sistem Ayarları',
        href: '/system-settings',
        icon: 'Settings',
        resource: 'system-settings',
        requiredAction: 'manage', // Sadece manage action'ı
      },
      {
        id: 'users',
        label: 'Kullanıcılar',
        href: '/users',
        icon: 'Users',
        resource: 'users',
      },
      {
        id: 'user_activity',
        label: 'Kullanıcı Aktiviteleri',
        href: '/user-activity',
        icon: 'Activity',
        resource: 'user-activity',
      },
      {
        id: 'announcements',
        label: 'Duyurular',
        href: '/announcements',
        icon: 'Bell',
        resource: 'announcements',
      },
      {
        id: 'menu_management',
        label: 'Yetki Yönetimi',
        href: '/menu-management',
        icon: 'Shield',
        resource: 'menu-management',
      },
      {
        id: 'rbac_management',
        label: 'RBAC Yönetimi',
        href: '/rbac-management',
        icon: 'Shield',
        resource: 'role-permissions',
        requiredAction: 'manage',
      },
    ],
  },

  // ============================================
  // PROFIL & BİLDİRİMLER (Bottom menu)
  // ============================================
  {
    id: 'profile',
    label: 'Profil',
    href: '/profile',
    icon: 'User',
    resource: 'dashboard', // Her kullanıcı erişebilir
  },
];

/**
 * Menu item'ı başarıyla render edilebilir mi?
 * Eğer permission'ı yoksa false döndür
 */
export const canViewMenuItem = (
  resource: Resource,
  requiredAction: 'view' | 'manage' = 'view'
): boolean => {
  // Bu fonksiyon AuthContext'te kullanılacak
  // usePermission hook'u tarafından call edilecek
  return true; // Implementation useMenuPermission hook'unda yapılacak
};
